import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  ChevronLeft, Loader2, Pencil, Upload, ChevronDown,
  CalendarClock, MoreHorizontal, Copy, Trash2, Save,
  FlaskConical, Clapperboard, Grid2x2, Music, Hand, Sparkles, ArrowLeftRight, ExternalLink, Info,
} from 'lucide-react'
import { Button } from '@meno/ui/components/ui/button'
import { ConfirmDialog } from '@meno/ui/components/ui/confirm-dialog'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@meno/ui/components/ui/dialog'
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '@meno/ui/components/ui/dropdown-menu'
import { usePageHeader } from '@meno/ui/components/layout/PageHeader'
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard'
import { getMix, updateMix, deleteMix, type Mix } from '@meno/ui/lib/api'
import MixIngredients, { type MixIngredientsValue } from '@/components/MixIngredients'
import MixSequencePreview from '@/components/MixSequencePreview'
import { PublishDialog } from '@/components/PublishDialog'
import { publishMix } from '@/lib/companions'
import { TRANSITIONS } from '@/lib/transitions'
import { TransitionPreview, TransitionPreviewStyles } from '@/components/TransitionPreview'

const ingredientsFromMix = (m: Mix): MixIngredientsValue => ({
  playlistId: m.playlist_id ?? null,
  layoutId: m.layout_id ?? null,
  audioId: m.audio_id ?? null,
  audioPlaylistId: m.audio_playlist_id ?? null,
  stopScreenId: m.stop_screen_id ?? null,
  transition: m.transition ?? null,
})

// Signature of everything a save persists — same "buffer until Save/Publish"
// pattern as Playlist/Layout, so a Mix's UX matches theirs exactly.
function mixSig(m: Mix): string {
  return JSON.stringify({
    name: m.name,
    playlist_id: m.playlist_id, layout_id: m.layout_id,
    audio_id: m.audio_id, audio_playlist_id: m.audio_playlist_id,
    stop_screen_id: m.stop_screen_id, transition: m.transition,
  })
}

export default function MixDetailsPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  // Always ChevronLeft — a global CSS rule mirrors it under dir="rtl"; see
  // WizardNavButton.tsx for why swapping it here too double-flips it back.
  const ChevronIcon = ChevronLeft

  const [mix, setMix] = useState<Mix | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savedSig, setSavedSig] = useState('')
  const dirty = mix != null && mixSig(mix) !== savedSig
  const { guardOpen, guardNavigate, confirmLeave, cancelLeave } = useUnsavedChangesGuard(dirty)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [publishMode, setPublishMode] = useState<'publish' | 'schedule' | null>(null)
  // Publishing a mix isn't atomic — each set ingredient replays against its
  // own publish endpoint and replaces its own channel on the target screens.
  // Surface that explicitly before opening the real PublishDialog, since it's
  // easy to assume "publish" pushes one bundled unit the way every other
  // content kind does.
  const [pendingPublishMode, setPendingPublishMode] = useState<'publish' | 'schedule' | null>(null)

  const [titleDraft, setTitleDraft] = useState('')
  const [editingTitle, setEditingTitle] = useState(false)
  const titleInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!id) return
    setLoading(true)
    getMix(Number(id))
      .then(r => { setMix(r.data); setSavedSig(mixSig(r.data)) })
      .catch(() => { toast.error(t('common.loadFailed', 'Failed to load')); navigate('/content-hub?tab=links') })
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const ingredients = mix ? ingredientsFromMix(mix) : {
    playlistId: null, layoutId: null, audioId: null, audioPlaylistId: null, stopScreenId: null, transition: null,
  }

  // Buffered locally; persisted on Save / Publish like the other detail pages.
  const onIngredientsChange = (next: MixIngredientsValue) => {
    if (!mix) return
    setMix({
      ...mix,
      playlist_id: next.playlistId, layout_id: next.layoutId,
      audio_id: next.audioId, audio_playlist_id: next.audioPlaylistId,
      stop_screen_id: next.stopScreenId, transition: next.transition,
    })
  }

  const saveChanges = async (): Promise<Mix | null> => {
    if (!mix) return null
    setSaving(true)
    try {
      const { data } = await updateMix(mix.id, {
        name: mix.name,
        playlist_id: mix.playlist_id, layout_id: mix.layout_id,
        audio_id: mix.audio_id, audio_playlist_id: mix.audio_playlist_id,
        stop_screen_id: mix.stop_screen_id, transition: mix.transition,
      })
      setMix(data)
      setSavedSig(mixSig(data))
      return data
    } catch {
      toast.error(t('common.updateFailed', 'Failed to update'))
      return null
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!mix) return
    try {
      await deleteMix(mix.id)
      toast.success(t('mix.deleted', 'Mix deleted'))
      navigate('/content-hub?tab=links')
    } catch {
      toast.error(t('common.deleteFailed', 'Failed to delete'))
    }
    setDeleteOpen(false)
  }

  const handleDuplicate = () => {
    toast.info(t('playlist.duplicateSoon', 'Duplicate is coming soon.'))
  }

  // Flush any buffered edits first so the pre-publish warning (and the
  // publish itself) reflect what's actually about to go out, not stale
  // picks from before the last edit.
  const openPublishWarning = async (targetMode: 'publish' | 'schedule') => {
    if (dirty) await saveChanges()
    setPendingPublishMode(targetMode)
  }

  const startTitleEdit = () => {
    if (!mix) return
    setTitleDraft(mix.name)
    setEditingTitle(true)
    setTimeout(() => titleInputRef.current?.focus(), 0)
  }
  const commitTitleEdit = () => {
    if (!mix) return
    const next = titleDraft.trim()
    setEditingTitle(false)
    if (!next || next === mix.name) return
    setMix({ ...mix, name: next })
  }

  const hasAnyIngredient = !!mix && (
    mix.playlist_id != null || mix.layout_id != null ||
    mix.audio_id != null || mix.audio_playlist_id != null ||
    mix.stop_screen_id != null || !!mix.transition
  )

  // What publishing this mix will actually do — one row per ingredient
  // that's set, each independently replacing its own channel on the target
  // screens, plus the actual item being pushed. Feeds the pre-publish
  // warning below.
  type PublishItem = { key: string; icon: typeof Music; label: string; value: string }
  const publishItems: PublishItem[] = mix ? ([
    (mix.playlist_id != null || mix.layout_id != null) && {
      key: 'content', icon: Clapperboard,
      label: t('mix.mainContent', 'Main content'),
      value: mix.playlist_name ?? mix.layout_name ?? t('mix.playlist', 'Playlist'),
    },
    (mix.audio_id != null || mix.audio_playlist_id != null) && {
      key: 'audio', icon: Music,
      label: t('companions.audio', 'Audio'),
      value: mix.audio_name ?? mix.audio_playlist_name ?? t('companions.audio', 'Audio'),
    },
    mix.stop_screen_id != null && {
      key: 'stopScreen', icon: Hand,
      label: t('companions.stopScreen', 'Stop screen'),
      value: mix.stop_screen_name ?? t('companions.stopScreen', 'Stop screen'),
    },
    mix.transition && {
      key: 'transition', icon: Sparkles,
      label: t('companions.transition', 'Transition'),
      value: TRANSITIONS.find(tr => tr.key === mix.transition)?.name ?? mix.transition,
    },
  ].filter((x): x is PublishItem => !!x)) : []

  const infoBar = useMemo(() => {
    if (!mix) return null
    return (
      <div className="flex items-center justify-between gap-3 w-full ps-2 pe-3">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => guardNavigate(() => navigate('/content-hub?tab=links'))}
            aria-label={t('common.back', 'Back')}
            className="w-9 h-9 flex items-center justify-center rounded-md text-foreground/70 hover:text-foreground hover:bg-muted transition-colors shrink-0"
          >
            <ChevronIcon className="w-4 h-4" />
          </button>
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <FlaskConical className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0 flex flex-col">
            <div className="flex items-center gap-1.5 min-w-0">
              {editingTitle ? (
                <input
                  ref={titleInputRef}
                  value={titleDraft}
                  onChange={e => setTitleDraft(e.target.value)}
                  onBlur={commitTitleEdit}
                  onKeyDown={e => {
                    if (e.key === 'Enter') commitTitleEdit()
                    if (e.key === 'Escape') { setEditingTitle(false); setTitleDraft(mix.name) }
                  }}
                  maxLength={150}
                  className="text-[15px] font-semibold leading-tight bg-transparent border-b border-primary outline-none min-w-0 w-48"
                />
              ) : (
                <>
                  <h1 className="text-[15px] font-semibold text-foreground leading-tight truncate">{mix.name}</h1>
                  <button
                    type="button"
                    onClick={startTitleEdit}
                    aria-label={t('common.rename', 'Rename')}
                    className="w-6 h-6 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {dirty && (
            <Button onClick={saveChanges} disabled={saving} className="gap-2 h-9">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {t('common.saveChanges', 'Save changes')}
            </Button>
          )}
          <div className="flex items-stretch h-9 rounded-md overflow-hidden border border-border bg-white text-foreground">
            <button
              type="button"
              onClick={() => openPublishWarning('publish')}
              disabled={!hasAnyIngredient}
              className="flex items-center gap-2 px-3 text-sm font-semibold hover:bg-muted transition-colors disabled:opacity-50"
            >
              <Upload className="w-4 h-4" />
              {t('playlist.publish', 'Publish')}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger
                aria-label={t('playlist.publishOptions', 'Publish options')}
                className="px-2 border-s border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors flex items-center"
              >
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onClick={() => openPublishWarning('schedule')} className="gap-2">
                  <CalendarClock className="w-4 h-4" />
                  {t('playlist.schedule', 'Schedule')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger
              aria-label={t('common.more', 'More')}
              className="w-9 h-9 flex items-center justify-center rounded-md border border-border bg-muted/40 text-foreground hover:bg-muted transition-colors"
            >
              <MoreHorizontal className="w-4 h-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={handleDuplicate} className="gap-2">
                <Copy className="w-4 h-4" />
                {t('common.duplicate', 'Duplicate')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setDeleteOpen(true)} className="gap-2 text-destructive focus:text-destructive">
                <Trash2 className="w-4 h-4" />
                {t('common.delete', 'Delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    )
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mix, editingTitle, titleDraft, dirty, saving, hasAnyIngredient])

  usePageHeader(infoBar ? { render: infoBar } : { title: t('contentHub.tabs.links', 'Lab') })

  if (loading) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
  }
  if (!mix) return null

  return (
    <div className="-m-4 sm:-m-[var(--space-l)] p-4 sm:p-[var(--space-l)] h-[calc(100dvh-3.5rem-1px)] flex flex-col bg-background">
      <TransitionPreviewStyles />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 flex-1 min-h-0">
        {/* Left — ingredients editor */}
        <div className="flex flex-col min-h-0 p-6 bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
          <h2 className="text-sm font-semibold mb-1 shrink-0">{t('mix.ingredients', 'Ingredients')}</h2>
          <p className="text-xs text-muted-foreground mb-4 shrink-0">
            {t('mix.ingredientsHint', 'Add the content, audio, stop screen and transition that publish together as this mix.')}
          </p>
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
            <MixIngredients value={ingredients} onChange={onIngredientsChange} />
          </div>
        </div>

        {/* Right — preview / summary */}
        <div className="flex flex-col min-h-0 p-6 bg-card rounded-2xl border border-border shadow-sm overflow-y-auto overflow-x-hidden">
          <h2 className="text-sm font-semibold mb-1">{t('common.preview', 'Preview')}</h2>
          {mix.playlist_id != null ? (
            <>
              <p className="text-xs text-muted-foreground mb-4">
                {t('mix.sequencePreviewHint', "Plays the mix's content in order, with its audio and transition style shown alongside.")}
              </p>
              <div className="space-y-3">
                <MixSequencePreview mix={mix} />
                <SummaryRow icon={Hand} label={t('companions.stopScreen', 'Stop screen')}
                  value={mix.stop_screen_name ?? t('companions.none', 'None')} />
              </div>
            </>
          ) : (
            <div className="space-y-3 mt-3">
              <MainContentPreview mix={mix} />
              <SummaryRow icon={Music} label={t('companions.audio', 'Audio')}
                value={mix.audio_name ?? mix.audio_playlist_name ?? t('companions.none', 'None')} />
              <SummaryRow icon={Hand} label={t('companions.stopScreen', 'Stop screen')}
                value={mix.stop_screen_name ?? t('companions.none', 'None')} />
              <div className="rounded-xl border border-border/60 bg-muted/30 p-3">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                  <ArrowLeftRight className="w-3.5 h-3.5" />
                  {t('transitions.title', 'Transition')}: <span className="font-medium text-foreground">{mix.transition ?? t('companions.none', 'None')}</span>
                </div>
                {mix.transition ? (
                  <TransitionPreview anim={`tr-${mix.transition}`} className="h-28 rounded-lg" logoClass="h-8" />
                ) : (
                  <p className="text-xs text-muted-foreground py-6 text-center">{t('mix.noTransition', 'No transition selected')}</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <PublishDialog<Mix>
        open={publishMode !== null}
        mode={publishMode ?? 'publish'}
        kind="mix"
        // Flush buffered edits first so the publish carries the just-edited
        // picks, then publish the mix's main content + companions together.
        publish={async payload => {
          const saved = await saveChanges()
          const toPublish = saved ?? mix
          await publishMix(toPublish, payload)
          return { data: toPublish }
        }}
        onClose={() => setPublishMode(null)}
        onPublished={() => setPublishMode(null)}
      />

      {/* Pre-publish info — calmer than ConfirmDialog's destructive styling,
          since nothing is being deleted; this is a heads-up, not a warning
          about data loss. Sized to comfortably fit the ingredient list. */}
      <Dialog open={pendingPublishMode !== null} onOpenChange={o => { if (!o) setPendingPublishMode(null) }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Info className="w-4 h-4 text-primary" />
              </div>
              <DialogTitle className="text-base">{t('mix.publishWarningTitle', 'This mix publishes several pieces separately')}</DialogTitle>
            </div>
            <DialogDescription className="ps-12">
              {t('mix.publishWarningDesc', "A mix isn't one bundled unit — each item below publishes independently to the selected screens. Anything not included here is left untouched, and any of these can be replaced on its own later without affecting the rest of the mix.")}
            </DialogDescription>
          </DialogHeader>
          <div className="ms-12 space-y-2">
            {publishItems.map(item => (
              <div key={item.key} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2.5">
                <span className="text-xs text-muted-foreground flex items-center gap-1.5 shrink-0">
                  <item.icon className="w-3.5 h-3.5" />
                  {item.label}
                </span>
                <span className="text-sm font-medium truncate">{item.value}</span>
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2 mt-1">
            <Button variant="outline" onClick={() => setPendingPublishMode(null)}>{t('common.cancel', 'Cancel')}</Button>
            <Button
              onClick={() => { setPublishMode(pendingPublishMode); setPendingPublishMode(null) }}
              className="gap-2"
            >
              <Upload className="w-4 h-4" />
              {t('common.continue', 'Continue')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deleteOpen}
        title={t('mix.deleteTitle', 'Delete mix')}
        description={t('mix.deleteDesc', 'This mix will be removed. The content it referenced is not deleted.')}
        onConfirm={handleDelete}
        onClose={() => setDeleteOpen(false)}
      />

      <ConfirmDialog
        open={guardOpen}
        title={t('common.unsavedTitle', 'Leave without publishing?')}
        description={t('common.unsavedDesc', "You didn't publish your changes. Your changes will be lost if you leave.")}
        confirmLabel={t('common.leaveDiscard', 'Leave & discard')}
        onConfirm={confirmLeave}
        onClose={cancelLeave}
      />
    </div>
  )
}

// Main-content preview card — a Mix's content differs from Playlist/Layout's
// own live preview (no media list to play through here), so this reads as a
// simple identity card with a link to open the actual playlist/layout.
function MainContentPreview({ mix }: { mix: Mix }) {
  const { t } = useTranslation()
  const hasPlaylist = mix.playlist_id != null
  const hasLayout = mix.layout_id != null
  const label = hasPlaylist ? mix.playlist_name : hasLayout ? mix.layout_name : null
  const href = hasPlaylist ? `/management/media/playlists/${mix.playlist_id}` : hasLayout ? `/management/layouts/${mix.layout_id}` : null
  const Icon = hasLayout ? Grid2x2 : Clapperboard

  return (
    <div className="rounded-xl border border-border/60 bg-muted/30 p-3">
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
        <Icon className="w-3.5 h-3.5" />
        {t('mix.mainContent', 'Main content')}
      </div>
      {label ? (
        <a
          href={href ?? '#'}
          target="_blank"
          rel="noreferrer"
          className="flex items-center justify-between gap-2 rounded-lg bg-card border border-border/60 px-3 py-2.5 hover:bg-muted/50 transition-colors"
        >
          <span className="text-sm font-medium truncate">{label}</span>
          <ExternalLink className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        </a>
      ) : (
        <p className="text-xs text-muted-foreground py-4 text-center">
          {t('mix.noMainContent', 'No playlist or layout selected')}
        </p>
      )}
    </div>
  )
}

function SummaryRow({ icon: Icon, label, value }: { icon: typeof Music; label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-border/60 bg-muted/30 px-4 py-3">
      <span className="text-xs text-muted-foreground flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> {label}</span>
      <span className="text-sm font-medium truncate">{value}</span>
    </div>
  )
}
